import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Container, Row, Col, Button, Spinner } from 'react-bootstrap';
import { Coin, Globe2, Pen, People, Tree, ChevronRight, Shield, Gear } from 'react-bootstrap-icons';
import '../../HomePage.css';
import './UKHomePage.css';
import DeskCarousel from '../carousel/DeskCarousel';
import TopicFeedCarousels, { departmentLinkFor, destinationFor } from '../carousel/TopicFeedCarousels';
import { unitedKingdomFeedsConfig, getUKRegionInfo } from '../../utils/unitedKingdomFeedsConfig';
import { getMobileFeedsForSection } from '../../utils/feedsTransformer';
import { getSectionsInfo } from '../../utils/multiCountryConfig.js';
import openCapacitorSite from '../../utils/browserUtils';
import FeedItem from '../feed/FeedItem';
import FeedSection from '../feed/FeedSection';
import LoadMoreButton from '../feed/LoadMoreButton';
import IntegratedSearchNavBar from '../navigation/IntegratedSearchNavBar';
import NotificationButton from '../notifications/NotificationButton';
import { useEffect, useState, useCallback } from 'react';
import useFeedData from '../../hooks/useFeedData';
import usePagedFeed from '../../hooks/usePagedFeed';
import useRankedFeed from '../../hooks/useRankedFeed';
import HomeFeedSection from '../feed/HomeFeedSection';
import EmptyNotificationsMessage from '../notifications/EmptyNotificationMessage';

// Section-icon visibility (removed from Preferences — icons off everywhere).
const useShowIcons = () => false;

// ═══════════════════════════════════════════════════════════════════
// COUNTRY CONFIG
// ═══════════════════════════════════════════════════════════════════
// Recents come from a single schema-level feed:
//   /posts/recent/schema?limit=<n>&skip=0&schema=united_kingdom
// It returns a recency-ordered LIST in one call, so every recents view renders
// one card PER ITEM in that list (not one card per config feed). Clicks resolve
// to /feed/united_kingdom/{collection table}, never to /feed/public/news_items.

const COUNTRY = 'uk';
const COUNTRY_SCHEMA = 'united_kingdom';
const RECENTS_PAGE = `/${COUNTRY}/recents`;
const HOME_PATH = '/uk';

// Recents paging. The first load is deliberately longer than a page so the list
// is worth scrolling before anyone has to press anything; each press of Load
// more then adds RECENTS_PAGE_SIZE on top. If the API caps its own page size
// below the initial count that's fine — usePagedFeed detects the end of the
// feed by result rather than by comparing counts against the request.
const RECENTS_FULL_COUNT = 40;
const RECENTS_PAGE_SIZE = 20;

// Turn a human-readable collection name into the snake_case table identifier
// the API/routing uses, e.g.
//   "Suprema Corte de Justicia de la Nación" -> "suprema_corte_de_justicia_de_la_nacion"
function slugifyCollection(name) {
  return (name || '')
    .toString()
    .normalize('NFD')                  // split accented chars into base + combining mark
    .replace(/[\u0300-\u036f]/g, '')   // strip the combining marks (á -> a, ó -> o, ñ -> n)
    .toLowerCase()
    .trim()
    .replace(/[’'`.,()]/g, '')         // drop punctuation that never appears in table names
    .replace(/\s+/g, '_')              // spaces -> underscores
    .replace(/[^a-z0-9_]/g, '');       // anything else -> removed
}

// Resolve the table a recents item should link to. Prefer an explicit `source`
// when present; otherwise derive it from `collection_name`.
function recentsTableFor(item, fallbackTable = '') {
  if (item && item.source) return item.source;
  const slug = slugifyCollection(item && item.collection_name);
  return slug || fallbackTable;
}

// The single schema-level recents feed.
const RECENT_FEED = unitedKingdomFeedsConfig.recent[0];

// Shared bits for a single recents item card.
const recentsCardProps = (item) => ({
  feedName: item.collection_name || item.source || 'Recents',
  schema: COUNTRY_SCHEMA,
  table: recentsTableFor(item),
  branch: RECENT_FEED.branch,
  topic: RECENT_FEED.topic,
  buttonName: RECENT_FEED.buttonName,
});

// ═══════════════════════════════════════════════════════════════════
// NATIONAL WEBSITE-UPDATES FEED (crawl-backed)
// ═══════════════════════════════════════════════════════════════════
// Recently published or changed pages across official UK government sites.
// Served by crawl.govglance.org rather than the main API — useFeedData routes
// on the `crawl` param, so nothing downstream has to know. This is the UK twin
// of FEDERAL_UPDATES_FEED on USHomePage; named "national" because the UK has no
// federal tier for `level` to name.
//
// It deliberately does NOT live in SECTION_MAP: there's no table behind it and
// no /uk/updates route, so it stays a home-page view (like Glance, Recents and
// Following) served from /uk.
//
// NOTE: `level` is omitted on purpose — unlike the US and Canada feeds there's
// no federal/state split to filter on here. Confirm the country code against
// crawl.govglance.org/api/v1/recent (uk vs gb); that's the only value to change.
const NATIONAL_UPDATES_FEED = {
  id: 'ukNationalUpdates',
  apiEndpoint: '&skip=0&crawl=recent&country=uk&since=7d',
  title: 'Web Crawl Updates',
  feedName: 'Web Crawl Updates',
  branch: 'Web Crawl Updates',
  topic: 'N/A',
  buttonName: 'Page',
};

// ═══════════════════════════════════════════════════════════════════
// ICONS
// ═══════════════════════════════════════════════════════════════════

const JudicalIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor">
    <path d="M160-120v-80h480v80H160Zm226-194L160-540l84-86 228 226-86 86Zm254-254L414-796l86-84 226 226-86 86Zm184 408L302-682l56-56 522 522-56 56Z"/>
  </svg>
);

const ClockIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
    <path d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71z"/>
    <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0"/>
  </svg>
);

const HealthIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
    <path d="m8 2.748-.717-.737C5.6.281 2.514.878 1.4 3.053.918 3.995.78 5.323 1.508 7H.43c-2.128-5.697 4.165-8.83 7.394-5.857q.09.083.176.171a3 3 0 0 1 .176-.17c3.23-2.974 9.522.159 7.394 5.856h-1.078c.728-1.677.59-3.005.108-3.947C13.486.878 10.4.28 8.717 2.01zM2.212 10h1.315C4.593 11.183 6.05 12.458 8 13.795c1.949-1.337 3.407-2.612 4.473-3.795h1.315c-1.265 1.566-3.14 3.25-5.788 5-2.648-1.75-4.523-3.434-5.788-5"/>
    <path d="M10.464 3.314a.5.5 0 0 0-.945.049L7.921 8.956 6.464 5.314a.5.5 0 0 0-.88-.091L3.732 8H.5a.5.5 0 0 0 0 1H4a.5.5 0 0 0 .416-.223l1.473-2.209 1.647 4.118a.5.5 0 0 0 .945-.049l1.598-5.593 1.457 3.642A.5.5 0 0 0 12 9h3.5a.5.5 0 0 0 0-1h-3.162z"/>
  </svg>
);

const InfrastructureIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
    <path d="M4 2.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zm3 0a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zm3.5-.5a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5v-1a.5.5 0 0 0-.5-.5z"/>
    <path d="M2 1a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1zm11 0H3v14h3v-2.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 .5.5V15h3z"/>
  </svg>
);

const JusticeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
    <path d="M8 3a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3"/>
    <path d="m5.93 6.704-.846 8.451a.768.768 0 0 0 1.523.203l.81-4.865a.59.59 0 0 1 1.165 0l.81 4.865a.768.768 0 0 0 1.523-.203l-.845-8.451A1.5 1.5 0 0 1 10.5 5.5L13 2.284a.796.796 0 0 0-1.239-.998L9.634 3.84a.7.7 0 0 1-.33.235c-.23.074-.665.176-1.304.176-.64 0-1.074-.102-1.305-.176a.7.7 0 0 1-.329-.235L4.239 1.286a.796.796 0 0 0-1.24.998l2.5 3.216c.317.316.475.758.43 1.204Z"/>
  </svg>
);

const ScienceIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
    <path d="M9.752 6.193c.599.6 1.73.437 2.528-.362.798-.799.96-1.932.362-2.531-.599-.6-1.73-.438-2.528.361-.798.8-.96 1.933-.362 2.532Z"/>
    <path d="M15.811 3.312c-.363 1.534-1.334 3.626-3.64 6.218l-.24 2.408a2.56 2.56 0 0 1-.732 1.526L8.817 15.85a.51.51 0 0 1-.867-.434l.27-1.899c.04-.28-.013-.593-.131-.956a9.42 9.42 0 0 0-.249-.657l-.082-.202c-.815-.197-1.578-.662-2.191-1.277-.614-.615-1.079-1.379-1.275-2.195l-.203-.083a9.556 9.556 0 0 0-.655-.248c-.363-.119-.675-.172-.955-.132l-1.896.27A.51.51 0 0 1 .15 7.17l2.382-2.386c.41-.41.947-.67 1.524-.734h.006l2.4-.238C9.005 1.55 11.087.582 12.623.208c.89-.217 1.59-.232 2.08-.188.244.023.435.06.57.093.067.017.12.033.16.045.184.06.279.13.351.295l.029.073a3.475 3.475 0 0 1 .157.721c.055.485.051 1.178-.159 2.065Zm-4.828 7.475.04-.04-.107 1.081a1.536 1.536 0 0 1-.44.913l-1.298 1.3.054-.38c.072-.506-.034-.993-.172-1.418a8.548 8.548 0 0 0-.164-.45c.738-.065 1.462-.38 2.087-1.006ZM5.205 5c-.625.626-.94 1.351-1.004 2.09a8.497 8.497 0 0 0-.45-.164c-.424-.138-.91-.244-1.416-.172l-.38.054 1.3-1.3c.245-.246.566-.401.91-.44l1.08-.107-.04.039Zm9.406-3.961c-.38-.034-.967-.027-1.746.163-1.558.38-3.917 1.496-6.937 4.521-.62.62-.799 1.34-.687 2.051.107.676.483 1.362 1.048 1.928.564.565 1.25.941 1.924 1.049.71.112 1.429-.067 2.048-.688 3.079-3.083 4.192-5.444 4.556-6.987.183-.771.18-1.345.138-1.713a2.835 2.835 0 0 0-.045-.283 3.078 3.078 0 0 0-.3-.041Z"/>
    <path d="M7.009 12.139a7.632 7.632 0 0 1-1.804-1.352A7.568 7.568 0 0 1 3.794 8.86c-1.102.992-1.965 5.054-1.839 5.18.125.126 3.936-.896 5.054-1.902Z"/>
  </svg>
);

// ═══════════════════════════════════════════════════════════════════
// SECTION MAPS
// ═══════════════════════════════════════════════════════════════════

const SECTION_MAP = {
  recent:        { title: 'Recents',        icon: <ClockIcon />,         branchKey: 'recent',        viewAllLink: RECENTS_PAGE },
  legislative:   { title: 'Legislative',    icon: <Pen />,               branchKey: 'legislative',   viewAllLink: '/uk/legislative' },
  executive:     { title: 'Executive',      icon: <People />,            branchKey: 'executive',     viewAllLink: '/uk/executive' },
  judicial:      { title: 'Judicial',       icon: <JudicalIcon />,       branchKey: 'judicial',      viewAllLink: '/uk/judicial' },
  defense:       { title: 'Defence',        icon: <Shield />,            branchKey: 'defense',       viewAllLink: '/uk/defense' },
  economy:       { title: 'Economy',        icon: <Coin />,              branchKey: 'economy',       viewAllLink: '/uk/economy' },
  environment:   { title: 'Environment',    icon: <Tree />,              branchKey: 'environment',   viewAllLink: '/uk/environment' },
  foreignAffairs:{ title: 'Foreign Affairs',icon: <Globe2 />,            branchKey: 'foreignAffairs',viewAllLink: '/uk/foreignaffairs' },
  health:        { title: 'Health',         icon: <HealthIcon />,        branchKey: 'health',        viewAllLink: '/uk/health' },
  infrastructure:{ title: 'Infrastructure', icon: <InfrastructureIcon />,branchKey: 'infrastructure',viewAllLink: '/uk/infrastructure' },
  justice:       { title: 'Justice',        icon: <JusticeIcon />,       branchKey: 'justice',       viewAllLink: '/uk/justice' },
  science:       { title: 'Science & Tech', icon: <ScienceIcon />,       branchKey: 'science',       viewAllLink: '/uk/science&tech' },
};

const DESKTOP_SECTION_MAP = {
  legislative:   { title: 'Legislative',    icon: <Pen />,               sections: unitedKingdomFeedsConfig.legislative,   viewAllLink: '/uk/legislative' },
  executive:     { title: 'Executive',      icon: <People />,            sections: unitedKingdomFeedsConfig.executive,     viewAllLink: '/uk/executive' },
  judicial:      { title: 'Judicial',       icon: <JudicalIcon />,       sections: unitedKingdomFeedsConfig.judicial,      viewAllLink: '/uk/judicial' },
  defense:       { title: 'Defence',        icon: <Shield />,            sections: unitedKingdomFeedsConfig.defense,       viewAllLink: '/uk/defense' },
  economy:       { title: 'Economy',        icon: <Coin />,              sections: unitedKingdomFeedsConfig.economy,       viewAllLink: '/uk/economy' },
  environment:   { title: 'Environment',    icon: <Tree />,              sections: unitedKingdomFeedsConfig.environment,   viewAllLink: '/uk/environment' },
  foreignAffairs:{ title: 'Foreign Affairs',icon: <Globe2 />,            sections: unitedKingdomFeedsConfig.foreignAffairs,viewAllLink: '/uk/foreignaffairs' },
  health:        { title: 'Health',         icon: <HealthIcon />,        sections: unitedKingdomFeedsConfig.health,        viewAllLink: '/uk/health' },
  infrastructure:{ title: 'Infrastructure', icon: <InfrastructureIcon />,sections: unitedKingdomFeedsConfig.infrastructure,viewAllLink: '/uk/infrastructure' },
  justice:       { title: 'Justice',        icon: <JusticeIcon />,       sections: unitedKingdomFeedsConfig.justice,       viewAllLink: '/uk/justice' },
  science:       { title: 'Science & Tech', icon: <ScienceIcon />,       sections: unitedKingdomFeedsConfig.science,       viewAllLink: '/uk/science&tech' },
};

const ALL_SECTIONS_ORDER = [
  'recent', 'legislative', 'executive', 'judicial',
  'defense', 'economy', 'environment', 'foreignAffairs',
  'health', 'infrastructure', 'justice', 'science',
];

// ═══════════════════════════════════════════════════════════════════
// URL <-> TOPIC
// ═══════════════════════════════════════════════════════════════════
// Topic URLs (/uk/legislative, ...) route to THIS page in App.js — see
// UK_SECTION_PATHS there, which must stay in sync with the viewAllLink values
// below. The path is the source of truth for those topics: pasting /uk/economy
// opens the home page with Economy already selected, and picking a topic in the
// filter bar pushes the matching path without leaving the page.
//
// Only topics present in BOTH maps are routed here, so nothing can land on an
// empty view. 'recent' keeps its own dedicated page (/uk/recents); Glance,
// Recents, Updates, Following and All Categories are home-page views and live
// at /uk.
const ROUTE_TO_TOPIC = Object.entries(SECTION_MAP).reduce((acc, [key, sec]) => {
  if (key === 'recent' || !DESKTOP_SECTION_MAP[key]) return acc;
  acc[sec.viewAllLink] = key;
  return acc;
}, {});

const SECTION_TOPICS = new Set(Object.values(ROUTE_TO_TOPIC));

// Trailing slashes and percent-encoding shouldn't decide which topic we show.
const normalizePath = (pathname) => {
  let path = pathname || '';
  try { path = decodeURIComponent(path); } catch (e) { /* leave as-is */ }
  return path.length > 1 ? path.replace(/\/+$/, '') : path;
};

const topicFromPath = (pathname) => ROUTE_TO_TOPIC[normalizePath(pathname)] || null;

// Same mapping SectionPage used, so notification topics keep matching the
// subscriptions people already have.
const getSectionBranch = (sectionKey) => {
  switch (sectionKey) {
    case 'defense':
    case 'defence':
      return 'defense';
    case 'foreign-affairs':
    case 'foreignaffairs':
    case 'foreignAffairs':
      return 'foreign affairs';
    case 'science-tech':
    case 'sciencetech':
    case 'science':
      return 'science & tech';
    default:
      return sectionKey;
  }
};

// Topic header: title, Learn more link, notification bell. Rendered only for
// topics that own a URL, so Glance / Recents / Updates / Following / All stay bare.
const SectionTopicHeader = ({ topicKey, isMobile }) => {
  const sec = SECTION_MAP[topicKey];
  if (!sec) return null;

  const info = (getSectionsInfo(COUNTRY) || {})[sec.branchKey] || {};
  const learnMoreLink = info.learnMoreLink || '';
  const branch = getSectionBranch(sec.branchKey);

  return (
    <div className="section-topic-header">
      <h3 className="section-topic-title">{sec.title}</h3>
      <div className="section-topic-actions">
        {/* {learnMoreLink && (
          <Link
            to="#"
            className="section-topic-learn-more"
            onClick={(e) => { e.preventDefault(); openCapacitorSite(learnMoreLink, '_blank'); }}
          >
            Learn more
          </Link>
        )} */}
        <NotificationButton
          feedId={`${sec.branchKey}-branch-${COUNTRY}`}
          feedName={`${sec.title} Updates`}
          branch={branch}
          topic={branch}
          country={COUNTRY}
          variant="outline-light"
          size="sm"
          showLabel={!isMobile}
        />
      </div>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════
// FOLLOWING FEED HELPERS
// ═══════════════════════════════════════════════════════════════════

const TOPIC_TO_SECTION = {
  legislative: 'legislative', executive: 'executive', judicial: 'judicial',
  defense: 'defense', economy: 'economy', elections: 'elections',
  environment: 'environment', foreignaffairs: 'foreignAffairs', health: 'health',
  science_and_tech: 'science', spending: 'spending', infrastructure: 'infrastructure',
  regulations: 'regulations', justice: 'justice',
};

const TOPIC_PREF_KEYS = {
  legislative: 'UKLegislativeNews', executive: 'UKExecutiveNews', judicial: 'UKJudicialNews',
  defense: 'UKDefenseNews', economy: 'UKEconomyNews', elections: 'UKElectionNews',
  environment: 'UKEnvironmentNews', foreignaffairs: 'UKForeignAffairsNews', health: 'UKHealthNews',
  science_and_tech: 'UKScienceTechNews', spending: 'UKSpendingNews',
  infrastructure: 'UKInfrastructureNews', regulations: 'UKRegulationsNews', justice: 'UKJusticeNews',
};

function getFollowedFeeds() {
  const followedTopics = Object.entries(TOPIC_PREF_KEYS)
    .filter(([_, prefKey]) => localStorage.getItem(prefKey) === 'true')
    .map(([topic]) => topic);
  const result = [];
  followedTopics.forEach((topicKey) => {
    const sectionKey = TOPIC_TO_SECTION[topicKey];
    if (!sectionKey) return;
    const entries = unitedKingdomFeedsConfig[sectionKey];
    if (!Array.isArray(entries)) return;
    entries.forEach((feed) => {
      if (feed.type === 'normal' && feed.apiEndpoint) {
        result.push(feed);
      } else if (feed.type === 'carousel' && feed.carouselItems) {
        feed.carouselItems.forEach((item) => {
          if (item.type === 'viewall' || item.type === 'member' || !item.apiEndpoint) return;
          result.push({ ...item, feedName: item.feedName || item.title, buttonName: item.buttonName || 'Document' });
        });
      }
    });
  });
  return { feeds: result, hasFollowedTopics: followedTopics.length > 0 };
}

// ═══════════════════════════════════════════════════════════════════
// RECENTS RENDERING (one card per item from the single schema feed)
// ═══════════════════════════════════════════════════════════════════

// Mobile vertical list of recents items (used in the dedicated recents view).
// The list grows in place behind a Load more button; paging and end-of-feed
// detection live in usePagedFeed, shared with the other country home pages.
const MobileRecentsList = ({ count = RECENTS_FULL_COUNT }) => {
  const { items, loading, isFirstLoad, hasMore, loadMore } = usePagedFeed(
    RECENT_FEED.apiEndpoint,
    { initialCount: count, pageSize: RECENTS_PAGE_SIZE }
  );
  if (isFirstLoad) return (
    <div className="d-flex justify-content-center py-4"><Spinner animation="grow" variant="light" /></div>
  );
  if (items.length === 0) return null;
  return (
    <div>
      {items.map((item, idx) => {
        const p = recentsCardProps(item);
        return (
          <div key={item.id || idx} className="mb-4">
            <FeedItem item={item} feedName={p.feedName} branch={p.branch} topic={p.topic} buttonName={p.buttonName} schema={p.schema} table={p.table} expanded={false} variant="homepage" isRecentsItem={true} />
          </div>
        );
      })}
      {hasMore && <LoadMoreButton onLoadMore={loadMore} loading={loading} />}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// CAROUSEL COMPONENTS (non-recents branches)
// ═══════════════════════════════════════════════════════════════════

const MobileCarouselCard = ({ feed }) => {
  const { data, loading, error } = useFeedData(feed.apiParams, 1);
  if (loading) return <div className="mobile-carousel-loading"><Spinner animation="grow" variant="light" /></div>;
  if (error || !data || data.length === 0) return <div className="mobile-carousel-empty"><p>{error ? 'Error loading content' : 'No content available'}</p></div>;
  const urlParams = new URLSearchParams(feed.apiParams);
  const schema = urlParams.get('schema') || 'united_kingdom';
  const table = urlParams.get('table');
  return (
    <div style={{ height: '160px', minHeight: '160px', overflow: 'hidden' }}>
      <FeedItem item={data[0]} feedName={feed.name} branch={feed.feedBranch} topic={feed.feedTopic} buttonName={feed.buttonName} schema={schema} table={table} expanded={false} variant="homepage" />
    </div>
  );
};

const HomePageBranchCarousel = ({ title, icon, branchKey, viewAllLink, isFirstCarousel = false }) => {
  const navigate = useNavigate();
  const showIcons = useShowIcons();
  const allBranchFeeds = getMobileFeedsForSection(unitedKingdomFeedsConfig, branchKey).filter(f => f.showOnHomePage !== false);
  // A feed inside a department (a ministry, an office) goes to that department's
  // page; everything else to its own full-feed view.
  const handleFeedClick = (feed) => {
    const target = departmentLinkFor(feed) || destinationFor(feed, COUNTRY_SCHEMA);
    if (!target) return;
    window.scrollTo(0, 0);
    navigate(target);
  };
  return (
    <div className={`homepage-branch-carousel ${isFirstCarousel ? 'homepage-first-carousel' : ''}`}>
      <div className="branch-header">
        <div className="branch-title" onClick={() => navigate(viewAllLink)}>{showIcons && icon}<span>{title}</span><ChevronRight className="branch-chevron" /></div>
      </div>
      <div className="carouselGroupContainer news-section">
        <div className="scroll-container">
          <div className="gradient-left" /><div className="gradient-right" />
          <div className="scroll-area"><div className="card-container">
            {allBranchFeeds.map((feed) => (
              <div key={feed.id} className="card-wrapper">
                {feed.name !== title && (
                  <div className="mobile-feed-heading-row">
                    <h5 className="feed-heading-mobile" onClick={() => handleFeedClick(feed)}>
                      <div className="title-container"><span className="heading-text2">{feed.name}</span><ChevronRight className="heading-chevron" /></div>
                    </h5>
                  </div>
                )}
                <div className="card-content"><div className="courtNewsMobile"><MobileCarouselCard feed={feed} /></div></div>
              </div>
            ))}
            <div className="card-wrapper view-all-card">
              <div id="viewFullPageButton"><Button id="viewFullLegislativePageButtonMobile" size="lg" onClick={() => navigate(viewAllLink)}>View all {title} news</Button></div>
            </div>
          </div></div>
        </div>
      </div>
    </div>
  );
};

// The topic view is the old section page: EVERY feed in the branch, not just the
// ones flagged for the home overview. showOnHomePage decides what appears in
// Glance and All Categories; filtering on it here hid most of each branch behind
// a couple of feeds. Grouping, department links and committee feeds all live in
// TopicFeedCarousels, shared with the other country home pages.
const TopicExpandedView = ({ topicKey }) => {
  const sec = SECTION_MAP[topicKey];
  if (!sec) return null;
  const feeds = getMobileFeedsForSection(unitedKingdomFeedsConfig, sec.branchKey);
  return <TopicFeedCarousels feeds={feeds} itemCount={5} fallbackSchema={COUNTRY_SCHEMA} />;
};

// ═══════════════════════════════════════════════════════════════════
// MOBILE CONTENT
// ═══════════════════════════════════════════════════════════════════

const MobileFeedList = ({ apiEndpoint, feedName, branch, topic, buttonName, maxItems = 10, isRecentsItem = false }) => {
  const { data, loading } = useFeedData(apiEndpoint, maxItems);
  const urlParams = new URLSearchParams((apiEndpoint || '').replace(/^[&?]/, ''));
  const schema = urlParams.get('schema') || '';
  const table = urlParams.get('table') || '';
  if (loading && (!data || data.length === 0)) return (
    <div className="d-flex justify-content-center py-4"><Spinner animation="grow" variant="light" /></div>
  );
  if (!data || data.length === 0) return null;
  return (
    <div>
      {data.map((item, idx) => (
        <div key={item.id || idx} className="mb-4">
          <FeedItem item={item} feedName={feedName} branch={branch} topic={topic} buttonName={buttonName || 'Document'} schema={schema} table={table} expanded={false} />
        </div>
      ))}
    </div>
  );
};

// ── National Updates (mobile): a single vertical list of recently published or
//    changed pages across UK government sites. One crawl request, no carousels. ──
const MobileUpdatesView = () => (
  <div style={{ padding: '0 10px' }}>
    <MobileFeedList
      apiEndpoint={NATIONAL_UPDATES_FEED.apiEndpoint}
      feedName={NATIONAL_UPDATES_FEED.feedName}
      branch={NATIONAL_UPDATES_FEED.branch}
      topic={NATIONAL_UPDATES_FEED.topic}
      buttonName={NATIONAL_UPDATES_FEED.buttonName}
      maxItems={20}
    />
  </div>
);

// ═══════════════════════════════════════════════════════════════════
// GLANCE (AI-ranked feed)
// ═══════════════════════════════════════════════════════════════════

// Glance full list (mobile): the default home view. Vertical list of ranked
// items, no heading. Falls back to the Recents list if the ranked feed can't
// load.
const MobileGlance = () => {
  const { items, loading, error } = useRankedFeed(COUNTRY);
  return (
    <>
      {loading && (<div className="d-flex justify-content-center py-4"><Spinner animation="grow" variant="light" /></div>)}
      {!loading && error && (<div style={{ padding: '0 10px' }}><MobileRecentsList count={RECENTS_FULL_COUNT} /></div>)}
      {!loading && !error && (
        <div style={{ padding: '0 10px' }}>
          {items.map((item, idx) => (
            <div key={item.id || idx} className="mb-4">
              <FeedItem item={item} feedName={item.collection_name || item.source || 'Glance'} branch={item.branch || 'N/A'} topic={item.topic || 'N/A'} buttonName={item.buttonName || 'Document'} schema={COUNTRY_SCHEMA} table={item.source || ''} expanded={false} />
            </div>
          ))}
        </div>
      )}
    </>
  );
};

// All view (mobile): topic carousels only. No Glance, Recents, or report.
const MobileAllView = () => (
  <>
    {ALL_SECTIONS_ORDER.filter((key) => key !== 'recent').map((key) => {
      const sec = SECTION_MAP[key];
      if (!sec) return null;
      return <HomePageBranchCarousel key={key} title={sec.title} icon={sec.icon} branchKey={sec.branchKey} viewAllLink={sec.viewAllLink} />;
    })}
  </>
);

const MobileContent = ({ activeTopic, activeFeed }) => {
  const navigate = useNavigate();

  // ── Single feed selected → vertical list for that one feed ──
  if (activeFeed && activeFeed.id && activeFeed.apiEndpoint) {
    return (
      <div style={{ padding: '0 10px' }}>
        <div
          style={{ padding: '8px 10px 8px', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}
          onClick={() => {
            const target = destinationFor(activeFeed, COUNTRY_SCHEMA);
            if (target) navigate(target);
          }}
        >
          <span style={{ fontSize: '17px', fontWeight: 'bold', color: 'var(--text-primary)' }}>{activeFeed.title || activeFeed.feedName}</span>
          <ChevronRight style={{ width: '16px', height: '16px', color: 'var(--text-primary)' }} />
        </div>
        <MobileFeedList
          apiEndpoint={activeFeed.apiEndpoint}
          feedName={activeFeed.feedName || activeFeed.title}
          branch={activeFeed.branch}
          topic={activeFeed.topic}
          buttonName={activeFeed.buttonName}
          maxItems={20}
        />
      </div>
    );
  }

  // ── Following feed ──
  if (activeTopic === 'following') {
    const { feeds: followedFeeds, hasFollowedTopics } = getFollowedFeeds();
    const settingsBtn = (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '8px 0 12px' }}>
        {/* <Link to="/notifications/settings" style={{ marginLeft: 'auto', display: 'inline-flex' }}>
          <Button variant="link" style={{ color: 'var(--text-primary)', padding: '4px', border: 'none', background: 'none', boxShadow: 'none' }}>
            <Gear size={18} />
          </Button>
        </Link> */}
      </div>
    );
    if (!hasFollowedTopics || followedFeeds.length === 0) {
      return (
        <div style={{ padding: '0 10px' }}>
          {settingsBtn}
          <EmptyNotificationsMessage />
        </div>
      );
    }
    return (
      <div style={{ padding: '0 10px' }}>
        {settingsBtn}
        <HomeFeedSection feeds={followedFeeds} itemsPerFeed={10} searchQuery="" />
      </div>
    );
  }

  if (activeTopic === 'glance') {
    return <MobileGlance />;
  }

  // ── Website updates (crawl-backed) ──
  if (activeTopic === 'updates') {
    return <MobileUpdatesView />;
  }

  if (activeTopic === 'all') {
    return <MobileAllView />;
  }

  if (activeTopic === 'recent') {
    return (
      <div style={{ padding: '0 10px' }}>
        <MobileRecentsList count={RECENTS_FULL_COUNT} />
      </div>
    );
  }

  return <TopicExpandedView topicKey={activeTopic} />;
};

// ═══════════════════════════════════════════════════════════════════
// DESKTOP HELPERS
// ═══════════════════════════════════════════════════════════════════

function flattenSectionFeeds(categoryFeeds) {
  if (!Array.isArray(categoryFeeds)) return [];
  const result = [];
  categoryFeeds.forEach((feed) => {
    if (feed.type === 'normal') {
      result.push(feed);
    } else if (feed.type === 'carousel' && feed.carouselItems) {
      feed.carouselItems.forEach((item) => {
        if (item.type === 'viewall' || item.type === 'member') return;
        result.push({
          ...item,
          branch: item.branch || feed.branch,
          topic: item.topic || feed.topic,
          viewAllLink: item.viewAllLink || feed.departmentLink,
        });
      });
    }
  });
  return result;
}

// ═══════════════════════════════════════════════════════════════════
// DESKTOP CONTENT
// ═══════════════════════════════════════════════════════════════════

// Desktop dedicated recents view: vertical list of item cards from the one feed,
// growing in place behind the same Load more button as mobile. View More on each
// card opens that item's own feed, matching the Mexico and Canada lists.
const DesktopRecentsFullList = ({ count = RECENTS_FULL_COUNT }) => {
  const { items, loading, isFirstLoad, hasMore, loadMore } = usePagedFeed(
    RECENT_FEED.apiEndpoint,
    { initialCount: count, pageSize: RECENTS_PAGE_SIZE }
  );
  if (isFirstLoad) return (
    <div className="d-flex justify-content-center py-4"><Spinner animation="grow" variant="light" /></div>
  );
  if (items.length === 0) return null;
  return (
    <>
      <div className="feed-items">
        {items.map((item, idx) => {
          const p = recentsCardProps(item);
          return (
            <FeedItem
              key={item.id || idx}
              item={item}
              feedName={p.feedName}
              branch={p.branch}
              topic={p.topic}
              buttonName={p.buttonName}
              schema={p.schema}
              table={p.table}
              variant="homepage"
              showViewMore={true}
              viewMoreUrl={p.table ? `/feed/${p.schema}/${p.table}` : null}
              isRecentsItem={true}
            />
          );
        })}
      </div>
      {hasMore && <LoadMoreButton onLoadMore={loadMore} loading={loading} />}
    </>
  );
};

// ── National Updates (desktop): same narrow single-column treatment as Glance
//    and Recents. No View more — there's no dedicated updates page yet. ──
const DesktopUpdatesView = () => (
  <Container fluid className="desktop-list-wrap">
    <div id="recentsCon" className="desktop-single-feed">
      <Col id="homeCol">
        <div id="columnLefttPadding">
          <FeedSection
            apiEndpoint={NATIONAL_UPDATES_FEED.apiEndpoint}
            feedName={NATIONAL_UPDATES_FEED.feedName}
            branch={NATIONAL_UPDATES_FEED.branch}
            topic={NATIONAL_UPDATES_FEED.topic}
            buttonName={NATIONAL_UPDATES_FEED.buttonName}
            maxItems={20}
            showViewMore={false}
          />
        </div>
      </Col>
    </div>
  </Container>
);

// Glance full list (desktop): the default home view. Stacked ranked items, no
// heading. Falls back to the Recents list if the ranked feed can't load.
const DesktopGlance = () => {
  const { items, loading, error } = useRankedFeed(COUNTRY);
  return (
    <Container fluid className="desktop-list-wrap">
      <div id="recentsCon" className="desktop-single-feed">
        <Col id="homeCol">
          <div id="columnLefttPadding">
            {loading && (<div className="d-flex justify-content-center py-5"><Spinner animation="grow" variant="light" /></div>)}
            {!loading && error && <DesktopRecentsFullList count={RECENTS_FULL_COUNT} />}
            {!loading && !error && items.map((item, idx) => (
              <FeedItem key={item.id || idx} item={item} feedName={item.collection_name || item.source || 'Glance'} branch={item.branch || 'N/A'} topic={item.topic || 'N/A'} buttonName={item.buttonName || 'Document'} schema={COUNTRY_SCHEMA} table={item.source || ''} expanded={false} />
            ))}
          </div>
        </Col>
      </div>
    </Container>
  );
};

// All view (desktop): topic-carousel grid only. No Glance, Recents, or report.
const DesktopAllView = () => {
  const showIcons = useShowIcons();
  const carouselKeys = ALL_SECTIONS_ORDER.filter(k => k !== 'recent');
  const rows = [];
  for (let i = 0; i < carouselKeys.length; i += 3) rows.push(carouselKeys.slice(i, i + 3));
  return (
    <Container fluid>
      {rows.map((row, ri) => (
        <Row id="branchesRow" key={ri}>
          {row.map((key) => {
            const sec = DESKTOP_SECTION_MAP[key];
            if (!sec || !sec.sections) return null;
            return <Col md={4} key={key}><DeskCarousel title={sec.title} icon={showIcons ? sec.icon : null} sections={sec.sections} viewAllLink={sec.viewAllLink} intervalDuration={30000} /></Col>;
          })}
        </Row>
      ))}
    </Container>
  );
};

const DesktopContent = ({ activeTopic, activeFeed }) => {
  const navigate = useNavigate();
  const showIcons = useShowIcons();

  // ── Glance (AI-ranked) → default home view ──
  if (activeTopic === 'glance') {
    return <DesktopGlance />;
  }

  // ── Website updates (crawl-backed) ──
  if (activeTopic === 'updates') {
    return <DesktopUpdatesView />;
  }

  // ── Following feed ──
  if (activeTopic === 'following') {
    const { feeds: followedFeeds, hasFollowedTopics } = getFollowedFeeds();
    const desktopHeader = (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: '14px' }}>
        {/* <Link to="/notifications/settings" style={{ marginLeft: 'auto', display: 'inline-flex' }}>
          <Button variant="link" style={{ color: 'var(--text-primary)', padding: '4px', border: 'none', background: 'none', boxShadow: 'none' }}>
            <Gear size={18} />
          </Button>
        </Link> */}
      </div>
    );
    if (!hasFollowedTopics) {
      return (
        <Container fluid className="desktop-list-wrap">
          <Row id="branchesRow">
            <Col md={12}>
              <div id="recentsCon" className="desktop-single-feed">
                <Col id="homeCol">
                  <div id="columnLefttPadding">
                    {desktopHeader}
                    <EmptyNotificationsMessage />
                  </div>
                </Col>
              </div>
            </Col>
          </Row>
        </Container>
      );
    }
    return (
      <Container fluid className="desktop-list-wrap">
        <Row id="branchesRow">
          <Col md={12}>
            <div id="recentsCon" className="desktop-single-feed">
              <Col id="homeCol">
                <div id="columnLefttPadding">
                  {desktopHeader}
                  <HomeFeedSection feeds={followedFeeds} itemsPerFeed={10} searchQuery="" />
                </div>
              </Col>
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  // ── Single feed selected → full-width with clickable heading ──
  if (activeFeed && activeFeed.id && activeFeed.apiEndpoint) {
    const feedUrl = destinationFor(activeFeed, COUNTRY_SCHEMA) || activeFeed.viewAllLink || null;
    return (
      <Container fluid>
        <Row id="branchesRow" style={{ marginBottom: '24px' }}>
          <Col md={12}>
            <div id="recentsCon" className="desktop-single-feed">
              <Col id="homeCol">
                <div id="columnLefttPadding">
                  {(activeFeed.title || activeFeed.feedName) && (
                    <h4
                      onClick={() => { if (feedUrl) { window.scrollTo(0, 0); navigate(feedUrl); } }}
                      style={{
                        fontWeight: 700, fontSize: '18px', color: 'var(--text-primary)',
                        marginBottom: '14px', paddingBottom: '8px',
                        borderBottom: '1px solid var(--border-color, rgba(128,128,128,0.2))',
                        cursor: feedUrl ? 'pointer' : 'default',
                        display: 'flex', alignItems: 'center', gap: '6px',
                      }}
                    >
                      {activeFeed.title || activeFeed.feedName}
                      <ChevronRight style={{ width: '18px', height: '18px' }} />
                    </h4>
                  )}
                  <FeedSection
                    apiEndpoint={activeFeed.apiEndpoint}
                    feedName={activeFeed.feedName || activeFeed.title}
                    branch={activeFeed.branch}
                    topic={activeFeed.topic}
                    buttonName={activeFeed.buttonName}
                    viewAllLink={activeFeed.viewAllLink}
                    maxItems={10}
                    showViewMore={true}
                  />
                </div>
              </Col>
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  if (activeTopic !== 'all') {
    // ── Recents → vertical list of items ──
    if (activeTopic === 'recent') {
      return (
        <Container fluid className="desktop-list-wrap">
          <div id="recentsCon" className="desktop-single-feed">
            <Col id="homeCol">
              <div id="columnLefttPadding">
                <DesktopRecentsFullList count={RECENTS_FULL_COUNT} />
              </div>
            </Col>
          </div>
        </Container>
      );
    }

    // ── Specific topic → 3-col grid of DeskCarousels ──
    const sec = DESKTOP_SECTION_MAP[activeTopic];
    if (!sec || !sec.sections) return null;
    const feeds = sec.sections;
    const rows = [];
    for (let i = 0; i < feeds.length; i += 3) rows.push(feeds.slice(i, i + 3));
    return (
      <Container fluid>
        {rows.map((row, ri) => (
          <Row id="branchesRow" key={ri} style={{ marginBottom: '24px' }}>
            {row.map((feed, fi) => {
              const feedTitle = feed.type === 'carousel'
                ? (feed.departmentTitle || feed.title)
                : (feed.title || feed.feedName);
              const feedViewAllLink = feed.type === 'carousel'
                ? (feed.departmentLink || sec.viewAllLink)
                : (feed.viewAllLink || sec.viewAllLink);
              return (
                <Col md={4} key={feed.id || fi}>
                  <DeskCarousel
                    title={feedTitle}
                    icon={showIcons ? sec.icon : null}
                    sections={[feed]}
                    viewAllLink={feedViewAllLink}
                    intervalDuration={30000}
                    showAll={true}
                    showFeedTitle={false}
                  />
                </Col>
              );
            })}
          </Row>
        ))}
      </Container>
    );
  }

  // ── All topics → topic-carousel grid only ──
  return <DesktopAllView />;
};

// ═══════════════════════════════════════════════════════════════════
// MAIN
// ═══════════════════════════════════════════════════════════════════

const UKHomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);
  // The path decides the topic on first paint, so /uk/economy renders the
  // Economy view straight away — no flash of Glance, no redirect.
  const [activeTopic, setActiveTopic] = useState(() => topicFromPath(location.pathname) || 'glance');
  const [activeFeed, setActiveFeed] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const check = () => {
      const ua = navigator.userAgent.toLowerCase();
      setIsMobile(/android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(ua) || window.innerWidth <= 768);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Keep the view in step with the URL — covers back/forward, in-app links and
  // a pasted address. Selecting a feed doesn't change the path, so this doesn't
  // fire and the feed selection survives.
  useEffect(() => {
    const urlTopic = topicFromPath(location.pathname);
    if (urlTopic) {
      setActiveTopic((prev) => (prev === urlTopic ? prev : urlTopic));
      setActiveFeed(null);
      return;
    }
    // Back on /uk: only reset when we came from a topic URL. Glance, Recents,
    // Updates, Following and All set their own topic and must not be clobbered.
    setActiveTopic((prev) => (SECTION_TOPICS.has(prev) ? 'glance' : prev));
  }, [location.pathname]);

  const handleSearch = useCallback((query) => {
    const trimmed = typeof query === 'string' ? query.trim() : (query?.target?.value || '').trim();
    setSearchQuery(trimmed);
    if (trimmed) navigate(`/search/uk?query=${encodeURIComponent(trimmed)}`);
  }, [navigate]);

  const handleFilterChange = useCallback(({ category, feed, stateKey }) => {
    if (stateKey) {
      navigate(`/uk/${stateKey}`);
      return;
    }

    const nextTopic = category || 'all';
    // Treat the synthetic 'recent_all' placeholder as no specific feed selected
    const hasFeed = !!(feed && feed.id && feed.id !== 'recent_all' && feed.apiEndpoint);

    setActiveTopic(nextTopic);
    setActiveFeed(hasFeed ? feed : null);

    // Put the topic in the address bar. Topics with a page of their own get it;
    // everything else goes back to /uk. Same page either way — App.js routes
    // both here.
    const target = SECTION_TOPICS.has(nextTopic) ? SECTION_MAP[nextTopic].viewAllLink : HOME_PATH;
    if (normalizePath(location.pathname) !== target) navigate(target);

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [navigate, location.pathname]);

  const feedFilterProps = {
    onFilterChange: handleFilterChange,
    currentSchema: 'united_kingdom',
    currentTable: '',
    currentFilterColumn: '',
    currentFilterString: '',
    currentCommittee: '',
    data: [],
    currentOrderBy: 'created_at',
    allowedOrderBy: [],
    onSortChange: () => {},
    currentCategory: activeTopic === 'all' ? null : activeTopic,
    hideSortButton: true,
    hideLevel: true,
    // This page renders its own view for every topic — Glance, All Categories,
    // Recents, Updates, Following, and a per-topic grid — so a Topic change must
    // NOT resolve a feed. With auto-select on, picking All Categories flattened
    // every category and handed back the first loadable feed, which landed in
    // the single-feed branch of Desktop/MobileContent and hid the All view
    // entirely; picking a topic did the same to the topic grid. Only an explicit
    // pick in the Feed dropdown sets activeFeed now.
    autoSelectFeed: false,
  };

  return (
    <>
      <IntegratedSearchNavBar
        searchQuery={searchQuery}
        onSearchChange={handleSearch}
        currentCountry="united_kingdom"
        placeholder="Search Gov Glance"
        feedFilterProps={feedFilterProps}
      />
      <div id="homebg" className="uk-homepage-root" style={{ paddingTop: 0 }}>
        {SECTION_TOPICS.has(activeTopic) && (
          <SectionTopicHeader topicKey={activeTopic} isMobile={isMobile} />
        )}
        {isMobile
          ? <MobileContent activeTopic={activeTopic} activeFeed={activeFeed} />
          : <DesktopContent activeTopic={activeTopic} activeFeed={activeFeed} />
        }
      </div>
      <style>{`
        .desktop-single-feed .feed-items > * + * { margin-top: 24px; }

        /* ─── Topic header (title + Learn more + notification bell) ─── */
        .section-topic-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 8px 20px 4px;
        }
        .section-topic-title {
          margin: 0;
          font-size: 17px;
          font-weight: 700;
          color: var(--text-primary);
        }
        .section-topic-actions { display: flex; align-items: center; gap: 12px; }
        .section-topic-learn-more {
          color: var(--accent-hover);
          font-size: 13px;
          white-space: nowrap;
          text-decoration: none;
        }
        .section-topic-learn-more:hover { text-decoration: underline; }

        @media screen and (min-width: 768px) {
          .section-topic-header { padding: 16px 24px 8px; }
          .section-topic-title { font-size: 22px; }
        }

        /* ─── Desktop single-column views (Glance, Recents, Updates, Following): narrow, centered ─── */
        .desktop-list-wrap {
          max-width: 700px;
          margin-left: auto;
          margin-right: auto;
        }
      `}</style>
    </>
  );
};

export default UKHomePage;